import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { guidFor } from '@ember/object/internals';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { t } from 'ember-intl';
import { notEq, eq } from 'ember-truth-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsFormToggleField,
  HdsFormSelectField,
  HdsButton,
} from '@hashicorp/design-system-components/components';
import { FlexGrid } from 'core/components';
import { boxMargin } from 'core/modifiers';
import RoleDefinitions from 'manage-access/components/manage-access/flyout/role-definitions';

/**
 *
 * ```
 * <Sso::FormInput::DefaultRoleToggleSelect
 *   @currentRolesLocation={{@currentRolesLocation}}
 *   @currentScopeLink={{@currentScopeLink}}
 *   @roleOptions={{array 'roles/viewer' 'roles/contributor' 'roles/admin'}}
 *   @selectedRole={{this.selectedRole}}
 *   @onRoleToggle={{this.handleRoleToggle}}
 *   @onRoleSelect={{this.handleRoleSelect}}
 * />
 * ```
 *
 * @class SsoFormInputDefaultRoleToggleSelect
 *
 */
export default class SsoFormInputDefaultRoleToggleSelect extends Component {
  @tracked showRoleDefinitionsFlyout = false;

  get id() {
    return guidFor(this);
  }

  handleToggle = (event) => {
    const checked = event.target.checked;
    this.args.onRoleToggle(checked ? 'roles/viewer' : 'none');
  };

  handleRoleSelect = (event) => {
    this.args.onRoleSelect(event.target.value);
  };
  <template>
    <HdsFormToggleField
      data-test-sso-form-input-default-role-toggle-select-inner-toggle
      name="assign-user-role"
      checked={{notEq @selectedRole "none"}}
      {{on "change" this.handleToggle}}
      as |F|
    >
      <F.Label>
        {{t "sso.form-input.default-role-toggle-select.toggle.label"}}
      </F.Label>

      <F.HelperText>
        {{t "sso.form-input.default-role-toggle-select.toggle.helper-text"}}
      </F.HelperText>
    </HdsFormToggleField>

    {{#if (notEq @selectedRole "none")}}
      <FlexGrid {{boxMargin "lg 0 0 0"}} as |Grid|>
        <Grid.Item @xs={{12}} @md={{6}} @lg={{8}}>
          <HdsFormSelectField
            data-test-sso-form-input-default-role-toggle-select-inner-select
            {{on "input" this.handleRoleSelect}}
            as |F|
          >
            <F.Label>
              {{t "sso.form-input.default-role-toggle-select.select.label"}}
            </F.Label>

            <F.Options>
              {{#each @roleOptions as |role|}}
                {{#if (eq role @selectedRole)}}
                  <option value={{role}} selected>
                    {{t (concat "sso.default-role-display." role)}}
                  </option>
                {{else}}
                  <option value={{role}}>
                    {{t (concat "sso.default-role-display." role)}}
                  </option>
                {{/if}}
              {{/each}}
            </F.Options>
          </HdsFormSelectField>
        </Grid.Item>

        <Grid.Item @xs={{12}} @md={{6}} @lg={{4}}>
          <div {{boxMargin "lg 0 0 0"}}>
            <HdsButton
              @color="tertiary"
              @icon="help"
              @iconPosition="leading"
              @isInline={{true}}
              @text={{t
                "sso.form-input.default-role-toggle-select.what-can-each-role-do"
              }}
              {{on "click" (set this "showRoleDefinitionsFlyout" true)}}
              data-test-roles-chart-toggle-button
            />
          </div>
        </Grid.Item>
      </FlexGrid>
    {{else}}
      <div {{boxMargin "2xl 0 2xl 0"}}>
        {{! NOTE: Leave some breathing room to insert the select below when toggled on ... }}
      </div>
    {{/if}}
    {{#if this.showRoleDefinitionsFlyout}}
      <RoleDefinitions
        @rolesLocation={{@currentRolesLocation}}
        @currentScopeLink={{@currentScopeLink}}
        @onClose={{set this "showRoleDefinitionsFlyout" false}}
      />
    {{/if}}
  </template>
}
